import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import Header_v2 from '../../components/layout_v2/Header_v2';
import Footer_v2 from '../../components/layout_v2/Footer_v2';

interface OverviewCard {
  label: string;
  value: string;
  icon: string;
}

interface Product {
  name: string;
  slug: string;
  mainImage: string;
  overviewCards: OverviewCard[];
}

interface ProductData {
  name: string;
  description: string;
  image: string;
  features: Array<{
    icon: string;
    title: string;
    description: string;
  }>;
}

export default function ProductsPage() {
  const { productSlug } = useParams<{ productSlug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  
  // Bölge tespiti - URL'den region bilgisini al
  const currentPath = location.pathname;
  const region = currentPath.startsWith('/ru/') ? 'ru' : currentPath.startsWith('/us/') ? 'us' : null;
  
  // Rusya ve Amerika sayfaları için her zaman İngilizce içerik göster
  const isRussian = region === 'ru';
  const isEnglish = region === 'ru' || region === 'us' || i18n.language === 'en';

  // Accordion state
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);

  const products: Product[] = [
    {
      name: isRussian ? 'Полностью закрытый волоконный лазер' : isEnglish ? 'Fully Enclosed Fiber Laser' : 'Kapalı Kabin Fiber Lazer',
      slug: 'kapali-kabin',
      mainImage: 'http://files.ajanlazer.com/wp-content/uploads/2025/12/ajanlazer.logo_.png',
      overviewCards: [
        { 
          label: isRussian ? 'Мощность лазера' : isEnglish ? 'Laser Power' : 'Lazer Gücü', 
          value: '3 / 6 / 12 / 20 kW', 
          icon: 'ri-flashlight-line' 
        },
        { 
          label: isRussian ? 'Точность позиционирования X/Y' : isEnglish ? 'X/Y Positioning Accuracy' : 'X/Y Pozisyon Hassasiyeti', 
          value: '±0.03 mm', 
          icon: 'ri-focus-3-line' 
        },
        { 
          label: isRussian ? 'Макс. скорость X/Y' : isEnglish ? 'X/Y Max Speed' : 'X/Y Maksimum Hız', 
          value: isRussian ? '88 м/мин' : isEnglish ? '88 m/min' : '88 metre/dakika', 
          icon: 'ri-speed-line' 
        },
        { 
          label: isRussian ? 'Конструкция кабины' : isEnglish ? 'Cabin Structure' : 'Kabin Yapısı', 
          value: isRussian ? 'Полностью закрытая • Безопасная работа' : isEnglish ? 'Fully Enclosed • Safe Operation' : 'Tam Kapalı • Güvenli Çalışma', 
          icon: 'ri-shield-check-line' 
        }
      ]
    },
    {
      name: isRussian ? 'Закрытый портальный волоконный лазер' : isEnglish ? 'Enclosed Gantry Fiber Laser' : 'Gezer Kabin Fiber Lazer',
      slug: 'gezer-kabin',
      mainImage: 'https://public.readdy.ai/ai/img_res/2e4c87a861e9ba69379214ad38c3c1ce.jpg',
      overviewCards: [
        { 
          label: isRussian ? 'Мощность лазера' : isEnglish ? 'Laser Power' : 'Lazer Gücü', 
          value: '3 / 6 / 12 / 20 kW', 
          icon: 'ri-flashlight-line' 
        },
        { 
          label: isRussian ? 'Точность позиционирования X/Y' : isEnglish ? 'X/Y Positioning Accuracy' : 'X/Y Pozisyon Hassasiyeti', 
          value: '±0.03 mm', 
          icon: 'ri-focus-3-line' 
        },
        { 
          label: isRussian ? 'Макс. скорость X/Y (холостой ход)' : isEnglish ? 'X/Y Max Speed (Idle)' : 'X/Y Maksimum Hız (Boşta)', 
          value: isRussian ? '30 м/мин' : isEnglish ? '30 m/min' : '30 metre/dakika', 
          icon: 'ri-speed-line' 
        },
        { 
          label: isRussian ? 'Конструкция кабины' : isEnglish ? 'Cabin Structure' : 'Kabin Yapısı', 
          value: isRussian ? 'Портальная кабина • Открытая система' : isEnglish ? 'Enclosed Gantry • Open System' : 'Gezer Kabin • Açık Sistem', 
          icon: 'ri-layout-line' 
        }
      ]
    },
    {
      name: isRussian ? 'Волоконный лазер для труб и профилей' : isEnglish ? 'Tube and Profile Fiber Laser' : 'Boru ve Profil Fiber Lazer',
      slug: 'boru-profil-kesim',
      mainImage: 'https://static.readdy.ai/image/9eedc5e2dd53bc7772895ba1e507e110/f498853daf2316de5fdcd5614fb9835f.png',
      overviewCards: [
        { 
          label: isRussian ? 'Мощность лазера' : isEnglish ? 'Laser Power' : 'Lazer Gücü', 
          value: '3 / 6 kW', 
          icon: 'ri-flashlight-line' 
        },
        { 
          label: isRussian ? 'Точность позиционирования X/Y' : isEnglish ? 'X/Y Positioning Accuracy' : 'X/Y Pozisyon Hassasiyeti', 
          value: '±0.02 mm / m', 
          icon: 'ri-focus-3-line' 
        },
        { 
          label: isRussian ? 'Макс. скорость X/Y' : isEnglish ? 'X/Y Max Speed' : 'X/Y Maksimum Hız', 
          value: isRussian ? '60 м/мин' : isEnglish ? '60 m/min' : '60 metre/dakika', 
          icon: 'ri-speed-line' 
        },
        { 
          label: isRussian ? 'Конструкция поворотной оси' : isEnglish ? 'Rotary Axis Structure' : 'Döner Eksen Yapısı', 
          value: isRussian ? 'Неограниченное вращение • Совместимость с трубами и профилями' : isEnglish ? 'Unlimited Rotation • Tube and Profile Compatible' : 'Sınırsız Rotasyon • Boru ve Profil Uyumlu', 
          icon: 'ri-refresh-line' 
        }
      ]
    }
  ];

  const [selectedProduct, setSelectedProduct] = useState<Product>(products[0]);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    if (productSlug) {
      const product = products.find(p => p.slug === productSlug);
      if (product) {
        setSelectedProduct(product);
      } else {
        // Bölgeye göre yönlendirme
        if (region === 'ru') {
          navigate('/ru/products/kapali-kabin', { replace: true });
        } else if (region === 'us') {
          navigate('/us/products/kapali-kabin', { replace: true });
        } else {
          navigate(isEnglish ? '/en/products/kapali-kabin' : '/products/kapali-kabin', { replace: true });
        }
      }
    } else {
      // Bölgeye göre yönlendirme
      if (region === 'ru') {
        navigate('/ru/products/kapali-kabin', { replace: true });
      } else if (region === 'us') {
        navigate('/us/products/kapali-kabin', { replace: true });
      } else {
        navigate(i18n.language === 'en' ? '/en/products/kapali-kabin' : '/products/kapali-kabin', { replace: true });
      }
    }
  }, [productSlug, navigate, isEnglish, region]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedProduct]);

  const handleProductSelect = (product: Product) => {
    setSelectedProduct(product);
    // Bölgeye göre URL oluştur
    if (region === 'ru') {
      navigate(`/ru/products/${product.slug}`);
    } else if (region === 'us') {
      navigate(`/us/products/${product.slug}`);
    } else {
      navigate(isEnglish ? `/en/products/${product.slug}` : `/products/${product.slug}`);
    }
  };

  const toggleAccordion = (section: string) => {
    setOpenAccordion(openAccordion === section ? null : section);
  };

  // Scroll event listener for scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Teklif İste butonuna tıklandığında ana sayfaya yönlendir ve forma scroll yap
  const handleRequestQuote = () => {
    // Bölgeye göre ana sayfaya git
    if (region === 'ru') {
      navigate('/ru');
    } else if (region === 'us') {
      navigate('/us');
    } else {
      navigate(i18n.language === 'en' ? '/en' : '/');
    }
    
    // Sayfa yüklendikten sonra forma scroll yap
    setTimeout(() => {
      const ctaSection = document.getElementById('cta-section');
      if (ctaSection) {
        const yOffset = -80;
        const y = ctaSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 300);
  };

  const scrollToCTA = () => {
    const ctaSection = document.getElementById('cta-section');
    if (ctaSection) {
      const yOffset = -80;
      const y = ctaSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    } else {
      // Bölgeye göre ana sayfaya git
      if (region === 'ru') {
        navigate('/ru');
      } else if (region === 'us') {
        navigate('/us');
      } else {
        navigate(i18n.language === 'en' ? '/en' : '/');
      }
      setTimeout(() => {
        const ctaElement = document.getElementById('cta-section');
        if (ctaElement) {
          const yOffset = -80;
          const y = ctaElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const productData: Record<string, ProductData> = {
    'kapali-kabin': {
      name: isEnglish ? 'Fully Enclosed Fiber Laser' : 'Kapalı Kabin Fiber Lazer',
      description: isEnglish 
        ? 'Safe and high-performance fiber laser cutting solutions suitable for serial production'
        : 'Seri üretime uygun, güvenli ve yüksek performanslı fiber lazer kesim çözümleri',
      image: 'https://static.readdy.ai/image/9eedc5e2dd53bc7772895ba1e507e110/48db2d4eadb135f04b847468fe9d54fe.jpeg',
      features: [
        {
          icon: 'ri-layout-grid-line',
          title: isEnglish ? 'Working Area' : 'Çalışma Alanı',
          description: isEnglish ? 'Various size options' : 'Farklı ebat seçenekleri'
        },
        {
          icon: 'ri-flashlight-line',
          title: isEnglish ? 'Power Unit' : 'Güç Ünitesi',
          description: isEnglish ? '3–20 kW Fiber Laser' : '3–20 kW Fiber Lazer'
        },
        {
          icon: 'ri-shield-check-line',
          title: isEnglish ? 'Cabin Structure' : 'Kabin Yapısı',
          description: isEnglish ? 'Fully Enclosed • Safe Operation' : 'Tam Kapalı • Güvenli Çalışma'
        },
        {
          icon: 'ri-star-line',
          title: isEnglish ? 'Advantage' : 'Avantaj',
          description: isEnglish ? 'Safe and precise serial production' : 'Güvenli ve hassas seri üretim'
        }
      ]
    },
    'gezer-kabin': {
      name: isEnglish ? 'Enclosed Gantry Fiber Laser' : 'Gezer Kabin Fiber Lazer',
      description: isEnglish 
        ? 'Flexible and powerful fiber laser solution for large-scale sheet metal cutting requiring open workspace'
        : 'Açık çalışma alanı gerektiren büyük ebatlı sacların kesimi için esnek ve güçlü fiber lazer çözümü',
      image: 'https://static.readdy.ai/image/9eedc5e2dd53bc7772895ba1e507e110/87536d91e938e8feb153e654c7ee1710.png',
      features: [
        {
          icon: 'ri-layout-line',
          title: isEnglish ? 'Structure' : 'Yapı',
          description: isEnglish ? 'Enclosed Gantry • Open System' : 'Gezer Kabin • Açık Sistem'
        },
        {
          icon: 'ri-flashlight-line',
          title: isEnglish ? 'Power Unit' : 'Güç Ünitesi',
          description: isEnglish ? '3–20 kW Fiber Laser' : '3–20 kW Fiber Lazer'
        },
        {
          icon: 'ri-speed-line',
          title: isEnglish ? 'Max Speed' : 'Maksimum Hız',
          description: isEnglish ? '30 m/min (Idle)' : '30 metre/dakika (Boşta)'
        },
        {
          icon: 'ri-star-line',
          title: isEnglish ? 'Advantage' : 'Avantaj',
          description: isEnglish ? 'Flexible production in wide workspace' : 'Geniş çalışma alanı için esnek üretim'
        }
      ]
    },
    'boru-profil-kesim': {
      name: isEnglish ? 'Tube and Profile Fiber Laser' : 'Boru ve Profil Fiber Lazer',
      description: isEnglish 
        ? 'High-precision fiber laser solution designed specifically for tube and profile cutting, suitable for continuous production'
        : 'Boru ve profil kesimine özel olarak tasarlanmış, yüksek hassasiyetli ve sürekli üretime uygun fiber lazer çözümü',
      image: 'https://static.readdy.ai/image/9eedc5e2dd53bc7772895ba1e507e110/f498853daf2316de5fdcd5614fb9835f.png',
      features: [
        {
          icon: 'ri-tools-line',
          title: isEnglish ? 'Structure' : 'Yapı',
          description: isEnglish ? 'Rotary Axis • Tube and Profile Compatible' : 'Döner Eksen • Boru ve Profil Uyumlu'
        },
        {
          icon: 'ri-flashlight-line',
          title: isEnglish ? 'Power Unit' : 'Güç Ünitesi',
          description: isEnglish ? '3–6 kW Fiber Laser' : '3–6 kW Fiber Lazer'
        },
        {
          icon: 'ri-star-line',
          title: isEnglish ? 'Advantage' : 'Avantaj',
          description: isEnglish ? 'High-precision cutting for serial production' : 'Seri üretim için yüksek hassasiyet kesim'
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {region ? <Header_v2 region={region} /> : <Header />}

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-20">
        {/* Premium Halo Background */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[95%] h-full bg-gradient-to-br from-slate-800/30 via-slate-700/20 to-slate-800/30 blur-3xl"></div>
        </div>

        <div className="flex relative z-10">
          {/* Sol Sidebar - Sabit */}
          <aside className="sticky top-20 h-[calc(100vh-80px)] w-96 bg-white border-r border-gray-200 overflow-y-auto shadow-lg">
            <div className="p-8 pt-6">
              <h2 className="text-base font-bold uppercase text-gray-500 mb-5 tracking-wider">
                {isRussian ? 'СЕМЕЙСТВА ПРОДУКЦИИ' : isEnglish ? 'PRODUCT FAMILIES' : 'ÜRÜN AİLELERİ'}
              </h2>
              
              <div className="space-y-3">
                {products.map((product) => (
                  <button
                    key={product.slug}
                    onClick={() => handleProductSelect(product)}
                    className={`w-full px-6 py-4 rounded-lg font-bold text-left transition-all duration-300 text-[22px] ${
                      selectedProduct.slug === product.slug
                        ? 'bg-[#DC2626] text-white shadow-lg'
                        : 'bg-[#E5E7EB] text-gray-700 hover:bg-[#D1D5DB]'
                    }`}
                  >
                    {product.name}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Sağ İçerik Alanı */}
          <div className="flex-1 px-16 py-8">
            <div className="max-w-[1320px] mx-auto">
              {/* Ürün Detay Kartı - Premium Shadow & Halo */}
              <div className="relative">
                {/* Soft Halo Effect */}
                <div className="absolute -inset-8 bg-gradient-to-br from-white/5 via-white/10 to-white/5 rounded-3xl blur-2xl"></div>
                
                {/* Main Card with Premium Shadow */}
                <div className="relative bg-white rounded-3xl shadow-[0_15px_50px_-10px_rgba(0,0,0,0.2)] border border-gray-100/50 p-8 mb-8 animate-fadeIn">
                  {/* Ürün Başlığı */}
                  <h1 className="text-6xl font-bold text-gray-900 mb-4 whitespace-nowrap leading-tight">
                    {selectedProduct.slug === 'kapali-kabin' 
                      ? (isRussian ? 'Полностью закрытый волоконный лазер' : isEnglish ? 'Fully Enclosed Fiber Laser' : 'Kapalı Kabin Fiber Lazer')
                      : selectedProduct.slug === 'gezer-kabin'
                      ? (isRussian ? 'Закрытый портальный волоконный лазер' : isEnglish ? 'Enclosed Gantry Fiber Laser' : 'Gezer Kabin Fiber Lazer')
                      : selectedProduct.slug === 'boru-profil-kesim'
                      ? (isRussian ? 'Волоконный лазер для труб и профилей' : isEnglish ? 'Tube and Profile Fiber Laser' : 'Boru ve Profil Fiber Lazer')
                      : selectedProduct.name
                    }
                  </h1>

                  {/* Ürün Açıklaması */}
                  {selectedProduct.slug === 'kapali-kabin' && (
                    <p className="text-[22px] text-gray-600 mb-5 leading-[1.7]">
                      {isRussian 
                        ? 'Разработан для серийного производства, требующего максимальной безопасности оператора и высокой точности.'
                        : isEnglish 
                        ? 'Designed for serial production requiring maximum operator safety and high precision.'
                        : 'Maksimum operatör güvenliği ve yüksek hassasiyet gerektiren seri üretimler için tasarlanmıştır.'}
                    </p>
                  )}
                  {selectedProduct.slug === 'gezer-kabin' && (
                    <p className="text-[22px] text-gray-600 mb-5 leading-[1.7]">
                      {isRussian 
                        ? 'Гибкое и мощное решение для волоконной лазерной резки крупноформатных листов, требующих открытого рабочего пространства.'
                        : isEnglish 
                        ? 'Flexible and powerful fiber laser solution for large-scale sheet metal cutting requiring open workspace.'
                        : 'Açık çalışma alanı gerektiren büyük ebatlı sacların kesimi için esnek ve güçlü fiber lazer çözümü.'}
                    </p>
                  )}
                  {selectedProduct.slug === 'boru-profil-kesim' && (
                    <p className="text-[22px] text-gray-600 mb-5 leading-[1.7]">
                      {isRussian 
                        ? 'Высокоточное решение для волоконной лазерной резки, специально разработанное для резки труб и профилей, подходящее для непрерывного производства.'
                        : isEnglish 
                        ? 'High-precision fiber laser solution designed specifically for tube and profile cutting, suitable for continuous production.'
                        : 'Boru ve profil kesimine özel olarak tasarlanmış, yüksek hassasiyetli ve sürekli üretime uygun fiber lazer çözümü.'}
                    </p>
                  )}

                  {/* Standart Çalışma Alanı Seçenekleri / Boru & Profil Kesim Kapasiteleri */}
                  {selectedProduct.slug === 'kapali-kabin' && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-600 uppercase tracking-wider mb-3">
                        {isRussian ? 'СТАНДАРТНЫЕ ВАРИАНТЫ РАБОЧЕЙ ЗОНЫ' : isEnglish ? 'STANDARD WORKING AREA OPTIONS' : 'STANDART ÇALIŞMA ALANI SEÇENEKLERİ'}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {region === 'us' 
                          ? ['5 × 10 ft', '5 × 20 ft', '6.5 × 10 ft', '6.5 × 20 ft', '8 × 20 ft'].map((size, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 text-lg font-medium rounded-full"
                              >
                                {size}
                              </span>
                            ))
                          : ['1500 × 3000 mm', '1500 × 6000 mm', '2000 × 3000 mm', '2000 × 6000 mm', '2500 × 6000 mm'].map((size, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 text-lg font-medium rounded-full"
                              >
                                {size}
                              </span>
                            ))
                        }
                      </div>
                    </div>
                  )}

                  {/* Gezer Kabin için Standart Çalışma Alanı Seçenekleri */}
                  {selectedProduct.slug === 'gezer-kabin' && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-600 uppercase tracking-wider mb-3">
                        {isRussian ? 'СТАНДАРТНЫЕ ВАРИАНТЫ РАБОЧЕЙ ЗОНЫ' : isEnglish ? 'STANDARD WORKING AREA OPTIONS' : 'STANDART ÇALIŞMA ALANI SEÇENEKLERİ'}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {region === 'us' 
                          ? ['5 × 10 ft', '6.5 × 13 ft', '10 × 40 ft'].map((size, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 text-lg font-medium rounded-full"
                              >
                                {size}
                              </span>
                            ))
                          : ['1500 × 3000 mm', '2000 × 4000 mm', '3000 × 12000 mm'].map((size, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 text-lg font-medium rounded-full"
                              >
                                {size}
                              </span>
                            ))
                        }
                      </div>
                    </div>
                  )}

                  {/* Boru ve Profil için Standart Boru ve Profil Kesim Kapasiteleri */}
                  {selectedProduct.slug === 'boru-profil-kesim' && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-600 uppercase tracking-wider mb-3">
                        {isRussian ? 'СТАНДАРТНЫЕ ПАРАМЕТРЫ РЕЗКИ ТРУБ И ПРОФИЛЕЙ' : isEnglish ? 'STANDARD TUBE AND PROFILE CUTTING CAPACITIES' : 'STANDART BORU VE PROFİL KESİM KAPASİTELERİ'}
                      </h3>
                      
                      {/* Boru Kesim Uzunluğu */}
                      <div className="mb-4">
                        <p className="text-lg text-gray-700 font-medium mb-2">
                          {isRussian ? 'Длина резки трубы:' : isEnglish ? 'Tube Cutting Length:' : 'Boru Kesim Uzunluğu:'} 
                          <span className="ml-2 text-gray-900">
                            {region === 'us' ? '20 ft / 40 ft' : '6000 mm / 12000 mm'}
                          </span>
                        </p>
                      </div>

                      {/* Seçenekler */}
                      <div className="space-y-3">
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <p className="text-lg font-semibold text-gray-900 mb-2">
                            {isRussian ? 'Вариант A:' : isEnglish ? 'Option A:' : 'Seçenek A:'}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <span className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 text-base font-medium rounded-full">
                              {region === 'us' ? 'Ø 0.8–9.4 in' : 'Ø 20–240 mm'}
                            </span>
                            <span className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 text-base font-medium rounded-full">
                              {region === 'us' ? '□ 0.8–6.7 in' : '□ 20–170 mm'}
                            </span>
                          </div>
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <p className="text-lg font-semibold text-gray-900 mb-2">
                            {isRussian ? 'Вариант B:' : isEnglish ? 'Option B:' : 'Seçenek B:'}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <span className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 text-base font-medium rounded-full">
                              {region === 'us' ? 'Ø 0.8–14.2 in' : 'Ø 20–360 mm'}
                            </span>
                            <span className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 text-base font-medium rounded-full">
                              {region === 'us' ? '□ 0.8–10 in' : '□ 20–254 mm'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Not */}
                      <p className="text-sm text-gray-500 mt-3 italic">
                        {isRussian 
                          ? 'Примечание: Варианты могут отличаться в зависимости от конфигурации станка.'
                          : isEnglish 
                          ? 'Note: Options may vary depending on machine configuration.' 
                          : 'Not: Seçenekler makine konfigürasyonuna göre değişebilir.'}
                      </p>
                    </div>
                  )}

                  {/* 4'lü Teknik Özellik Kutuları */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {selectedProduct.overviewCards.map((card, index) => (
                      <div
                        key={index}
                        className="bg-gradient-to-br from-red-50 to-white border border-red-100 rounded-xl p-2.5 hover:shadow-lg transition-all duration-300"
                      >
                        <div className="flex flex-col items-center text-center">
                          <div className="w-12 h-12 flex items-center justify-center mb-1.5">
                            <i className={`${card.icon} text-[#DC2626] text-3xl`}></i>
                          </div>
                          <div className="text-[14px] uppercase text-gray-500 font-medium tracking-wider mb-2 leading-[1.35]">
                            {card.label}
                          </div>
                          <div className={`${index === 3 ? 'text-[23px]' : 'text-[29px]'} font-semibold text-gray-900 leading-[1.35]`}>
                            {region === 'us' && index === 1 && selectedProduct.slug === 'kapali-kabin' 
                              ? '±0.001 in'
                              : region === 'us' && index === 2 && selectedProduct.slug === 'kapali-kabin'
                              ? '100 ft/min'
                              : region === 'us' && index === 1 && selectedProduct.slug === 'gezer-kabin'
                              ? '±0.001 in'
                              : region === 'us' && index === 2 && selectedProduct.slug === 'gezer-kabin'
                              ? '100 ft/min'
                              : region === 'us' && index === 1 && selectedProduct.slug === 'boru-profil-kesim'
                              ? '±0.001 in per 40 in'
                              : region === 'us' && index === 2 && selectedProduct.slug === 'boru-profil-kesim'
                              ? '200 ft/min'
                              : card.value
                            }
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Gezer Kabin için Ürün Görseli - 4 kutu ile Ana Bileşenler arasında */}
                  {selectedProduct.slug === 'gezer-kabin' && (
                    <div className="mb-8">
                      {/* Ana Ürün Görseli - Kapalı Kabin ile Birebir Aynı Format */}
                      <div className="bg-transparent rounded-xl p-8 flex items-center justify-center mb-2">
                        <img
                          src="http://files.ajanlazer.com/wp-content/uploads/2025/12/gezer_kabin_fonsuz.png"
                          alt={isRussian ? 'Закрытый портальный волоконный лазер' : 'Gezer Kabin Fiber Lazer'}
                          className="max-h-[700px] w-auto object-contain drop-shadow-2xl transition-transform duration-500 hover:scale-105"
                        />
                      </div>
                      {/* Teknik Bilgilendirme Metni */}
                      <p className="text-sm text-gray-400 text-center mb-8 font-normal">
                        {isRussian 
                          ? 'Изображение носит иллюстративный характер. Технические конфигурации могут отличаться в зависимости от модели и опций.'
                          : isEnglish 
                          ? 'Image is for illustration purposes. Technical configurations may vary depending on model and options.'
                          : 'Görsel temsilidir. Teknik konfigürasyonlar modele ve opsiyonlara göre değişiklik gösterebilir.'}
                      </p>
                    </div>
                  )}

                  {/* Ürün Görseli - Kapalı Kabin ve Boru & Profil için */}
                  {(selectedProduct.slug === 'kapali-kabin' || selectedProduct.slug === 'boru-profil-kesim') && (
                    <div className="mb-8">
                      {/* Ana Ürün Görseli - Kapalı Kabin ile Birebir Aynı Format */}
                      <div className="bg-transparent rounded-xl p-8 flex items-center justify-center mb-2">
                        <img
                          src={selectedProduct.mainImage}
                          alt={selectedProduct.name}
                          className="max-h-[900px] w-auto object-contain drop-shadow-2xl transition-transform duration-500 hover:scale-105"
                        />
                      </div>
                      {/* Teknik Bilgilendirme Metni */}
                      <p className="text-sm text-gray-400 text-center mb-8 font-normal">
                        {isRussian 
                          ? 'Изображение носит иллюстративный характер. Технические конфигурации могут отличаться в зависимости от модели и опций.'
                          : isEnglish 
                          ? 'Image is for illustration purposes. Technical configurations may vary depending on model and options.'
                          : 'Görsel temsilidir. Teknik konfigürasyonlar modele ve opsiyonlara göre değişiklik gösterebilir.'}
                      </p>
                    </div>
                  )}

                  {/* Ana Bileşenler Bölümü */}
                  {(selectedProduct.slug === 'kapali-kabin' || selectedProduct.slug === 'gezer-kabin' || selectedProduct.slug === 'boru-profil-kesim') && (
                    <div className="mb-8">
                      <div className="text-center mb-6">
                        <h3 className="text-5xl font-bold text-gray-900 mb-3 leading-tight">
                          {isRussian ? 'Основные компоненты' : isEnglish ? 'Main Components' : 'Ana Bileşenler'}
                        </h3>
                        <p className="text-[22px] text-gray-600 max-w-3xl mx-auto leading-[1.7]">
                          {selectedProduct.slug === 'boru-profil-kesim' 
                            ? (isRussian 
                              ? 'Основные подсистемы, используемые в системе волоконного лазера для труб и профилей, подобраны для обеспечения высокой точности и стабильной производительности резки.'
                              : isEnglish 
                              ? 'The main subsystems used in the Tube & Profile Fiber Laser system are selected for high precision and stable cutting performance.'
                              : 'Boru & Profil Fiber Lazer sisteminde kullanılan temel alt sistemler, yüksek hassasiyet ve kararlı kesim performansı için seçilmiştir.')
                            : (isRussian 
                              ? `Основные подсистемы, используемые в системе ${selectedProduct.slug === 'kapali-kabin' ? 'полностью закрытого' : 'портального'} волоконного лазера, тщательно подобраны для обеспечения высокой производительности и длительного срока службы.`
                              : isEnglish 
                              ? `The main subsystems used in the ${selectedProduct.slug === 'kapali-kabin' ? 'Fully Enclosed' : 'Enclosed Gantry'} Fiber Laser system are carefully selected for high performance and long-lasting use.`
                              : `${selectedProduct.slug === 'kapali-kabin' ? 'Kapalı Kabin' : 'Gezer Kabin'} Fiber Lazer sisteminde kullanılan temel alt sistemler, yüksek performans ve uzun ömürlü kullanım için özenle seçilmiştir.`)
                          }
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Fiber Lazer Güç Kaynağı */}
                        <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 flex flex-col h-full">
                          <div className="flex items-start gap-4 mb-4">
                            <div className="w-14 h-14 bg-gradient-to-br from-[#DC2626] to-[#B91C1C] rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-flashlight-line text-white text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h4 className="text-[32px] font-bold text-gray-900 mb-2 leading-tight">
                                {selectedProduct.slug === 'boru-profil-kesim'
                                  ? (isRussian ? 'Источник волоконного лазера (3–6 кВт)' : isEnglish ? 'Fiber Laser Power Source (3–6 kW)' : 'Fiber Lazer Güç Kaynağı (3–6 kW)')
                                  : (isRussian ? 'Источник волоконного лазера (3–20 кВт)' : isEnglish ? 'Fiber Laser Power Source (3–20 kW)' : 'Fiber Lazer Güç Kaynağı (3–20 kW)')
                                }
                              </h4>
                              <p className="text-lg text-gray-600 leading-[1.7]">
                                {isRussian 
                                  ? 'Обеспечивает превосходную производительность резки благодаря источникам волоконного лазера с высокой стабильностью, длительным сроком службы и энергоэффективностью.'
                                  : isEnglish 
                                  ? `Provides superior cutting performance with fiber laser sources offering high stability, long life and energy efficiency.`
                                  : `Yüksek stabilite, uzun ömür ve enerji verimliliği sunan fiber lazer kaynakları ile üstün kesim performansı sağlar.`}
                              </p>
                            </div>
                          </div>
                          <div className="mt-auto">
                            <img 
                              src="http://files.ajanlazer.com/wp-content/uploads/2025/12/Fiber-Lazer-Guc-Kaynagi.png"
                              alt={isRussian ? 'Источник волоконного лазера' : isEnglish ? 'Fiber Laser Power Source' : 'Fiber Lazer Güç Kaynağı'}
                              className="w-full h-32 object-contain rounded-lg bg-gray-50"
                            />
                          </div>
                        </div>

                        {/* Lazer Kafası */}
                        <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 flex flex-col h-full">
                          <div className="flex items-start gap-4 mb-4">
                            <div className="w-14 h-14 bg-gradient-to-br from-[#DC2626] to-[#B91C1C] rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-focus-2-line text-white text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h4 className="text-[32px] font-bold text-gray-900 mb-2 leading-tight">
                                {isRussian ? 'Лазерная головка с автофокусом (BOCHU)' : isEnglish ? 'Auto Focus Laser Head (BOCHU)' : 'Oto Focus Lazer Kafası (BOCHU)'}
                              </h4>
                              <p className="text-lg text-gray-600 leading-[1.7]">
                                {isRussian 
                                  ? 'Точная резка с автоматической фокусировкой при различной толщине материала.'
                                  : isEnglish 
                                  ? 'Precise cutting with automatic focusing on different material thicknesses.'
                                  : 'Farklı malzeme kalınlıklarında otomatik odaklama ile hassas kesim.'}
                              </p>
                            </div>
                          </div>
                          <div className="mt-auto">
                            <img 
                              src="http://files.ajanlazer.com/wp-content/uploads/2025/12/Oto-Focus-Lazer-Kafa-BOCI.png"
                              alt={isRussian ? 'Лазерная головка с автофокусом' : isEnglish ? 'Auto Focus Laser Head' : 'Oto Focus Lazer Kafası'}
                              className="w-full h-32 object-contain rounded-lg bg-gray-50"
                            />
                          </div>
                        </div>

                        {/* Optik İletim Sistemi */}
                        <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 flex flex-col h-full">
                          <div className="flex items-start gap-4 mb-4">
                            <div className="w-14 h-14 bg-gradient-to-br from-[#DC2626] to-[#B91C1C] rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-lightbulb-flash-line text-white text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h4 className="text-[32px] font-bold text-gray-900 mb-2 leading-tight">
                                {isRussian ? 'Оптическая система передачи' : isEnglish ? 'Optical Transmission System' : 'Optik İletim Sistemi'}
                              </h4>
                              <p className="text-lg text-gray-600 leading-[1.7]">
                                {isRussian 
                                  ? 'Максимальная производительность с минимальными потерями мощности благодаря волоконно-оптической структуре.'
                                  : isEnglish 
                                  ? 'Maximum performance with minimum power loss thanks to fiber optic structure.'
                                  : 'Fiber optik yapı sayesinde minimum güç kaybı ile maksimum performans.'}
                              </p>
                            </div>
                          </div>
                          <div className="mt-auto">
                            <img 
                              src="http://files.ajanlazer.com/wp-content/uploads/2025/12/Optik-Iletim-Sistemi.png"
                              alt={isRussian ? 'Оптическая система передачи' : isEnglish ? 'Optical Transmission System' : 'Optik İletim Sistemi'}
                              className="w-full h-32 object-contain rounded-lg bg-gray-50"
                            />
                          </div>
                        </div>

                        {/* FSCUT CNC Kontrol Sistemi */}
                        <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 flex flex-col h-full">
                          <div className="flex items-start gap-4 mb-4">
                            <div className="w-14 h-14 bg-gradient-to-br from-[#DC2626] to-[#B91C1C] rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-computer-line text-white text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h4 className="text-[32px] font-bold text-gray-900 mb-2 leading-tight">
                                {isRussian ? 'Система ЧПУ управления FSCUT' : isEnglish ? 'FSCUT CNC Control System' : 'FSCUT CNC Kontrol Sistemi'}
                              </h4>
                              <p className="text-lg text-gray-600 leading-[1.7]">
                                {selectedProduct.slug === 'boru-profil-kesim'
                                  ? (isRussian 
                                    ? 'Усовершенствованные алгоритмы управления, специально разработанные для резки труб и профилей.'
                                    : isEnglish 
                                    ? 'Advanced control algorithms specifically developed for tube and profile cutting.'
                                    : 'Boru ve profil kesimine özel gelişmiş kontrol algoritmaları.')
                                  : (isRussian 
                                    ? 'Удобный интерфейс и усовершенствованное управление резкой.'
                                    : isEnglish 
                                    ? 'User-friendly interface and advanced cutting control.'
                                    : 'Kullanıcı dostu arayüz ve gelişmiş kesim kontrolü.')
                                }
                              </p>
                            </div>
                          </div>
                          <div className="mt-auto">
                            <img 
                              src="http://files.ajanlazer.com/wp-content/uploads/2025/12/FSCUT-Lazer-Kontrol-Sistemi.png"
                              alt={isRussian ? 'Система ЧПУ управления FSCUT' : isEnglish ? 'FSCUT CNC Control System' : 'FSCUT CNC Kontrol Sistemi'}
                              className="w-full h-32 object-contain rounded-lg bg-gray-50"
                            />
                          </div>
                        </div>

                        {/* Soğutma Sistemi */}
                        <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 flex flex-col h-full">
                          <div className="flex items-start gap-4 mb-4">
                            <div className="w-14 h-14 bg-gradient-to-br from-[#DC2626] to-[#B91C1C] rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-temp-cold-line text-white text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h4 className="text-[32px] font-bold text-gray-900 mb-2 leading-tight">
                                {isRussian ? 'Система охлаждения (чиллер)' : isEnglish ? 'Cooling System (Chiller)' : 'Soğutma Sistemi (Chiller)'}
                              </h4>
                              <p className="text-lg text-gray-600 leading-[1.7]">
                                {isRussian 
                                  ? 'Стабильный контроль температуры для источника лазера и оптических компонентов.'
                                  : isEnglish 
                                  ? 'Stable temperature control for laser source and optical components.'
                                  : 'Lazer kaynağı ve optik bileşenler için kararlı sıcaklık kontrolü.'}
                              </p>
                            </div>
                          </div>
                          <div className="mt-auto">
                            <img 
                              src="http://files.ajanlazer.com/wp-content/uploads/2025/12/Sogutma-Sistemi-Chiller.png"
                              alt={isRussian ? 'Система охлаждения' : isEnglish ? 'Cooling System' : 'Soğutma Sistemi'}
                              className="w-full h-32 object-contain rounded-lg bg-gray-50"
                            />
                          </div>
                        </div>

                        {/* Elektrik Panosu */}
                        <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 flex flex-col h-full">
                          <div className="flex items-start gap-4 mb-4">
                            <div className="w-14 h-14 bg-gradient-to-br from-[#DC2626] to-[#B91C1C] rounded-lg flex items-center justify-center flex-shrink-0">
                              <i className="ri-plug-line text-white text-2xl"></i>
                            </div>
                            <div className="flex-1">
                              <h4 className="text-[32px] font-bold text-gray-900 mb-2 leading-tight">
                                {selectedProduct.slug === 'boru-profil-kesim'
                                  ? (isRussian ? 'Электрический шкаф (промышленный стандарт)' : isEnglish ? 'Electrical Panel (Industrial Standard)' : 'Elektrik Panosu')
                                  : (isRussian ? 'Электрический шкаф' : isEnglish ? 'Electrical Panel' : 'Elektrik Panosu')
                                }
                              </h4>
                              <p className="text-lg text-gray-600 leading-[1.7]">
                                {selectedProduct.slug === 'boru-profil-kesim'
                                  ? (isRussian 
                                    ? 'Совместимость с 380 В, безопасная и организованная электрическая инфраструктура.'
                                    : isEnglish 
                                    ? '380V compatible, safe and organized electrical infrastructure.'
                                    : '380V uyumlu, güvenli ve düzenli elektrik altyapısı.')
                                  : (isRussian 
                                    ? 'Безопасная и организованная электрическая инфраструктура в соответствии с промышленными стандартами.'
                                    : isEnglish 
                                    ? 'Safe and organized electrical infrastructure in accordance with industrial standards.'
                                    : 'Endüstriyel standartlara uygun, güvenli ve düzenli elektrik altyapısı.')
                                }
                              </p>
                            </div>
                          </div>
                          <div className="mt-auto">
                            <img 
                              src="http://files.ajanlazer.com/wp-content/uploads/2025/12/Elektrik-Panosu.png"
                              alt={isRussian ? 'Электрический шкаф' : isEnglish ? 'Electrical Panel' : 'Elektrik Panosu'}
                              className="w-full h-32 object-contain rounded-lg bg-gray-50"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Detaylı Teknik Özellikler */}
                  {(selectedProduct.slug === 'kapali-kabin' || selectedProduct.slug === 'gezer-kabin' || selectedProduct.slug === 'boru-profil-kesim') && (
                    <div className="mb-8">
                      <div className="text-center mb-6">
                        <h3 className="text-5xl font-bold text-gray-900 mb-3 leading-tight">
                          {isRussian ? 'Подробные технические характеристики' : isEnglish ? 'Detailed Technical Specifications' : 'Detaylı Teknik Özellikler'}
                        </h3>
                        <p className="text-[22px] text-gray-600 max-w-3xl mx-auto leading-[1.7]">
                          {selectedProduct.slug === 'boru-profil-kesim'
                            ? (isRussian 
                              ? 'Ниже представлены технические данные механической, электронной и двигательной инфраструктуры системы волоконного лазера для труб и профилей.'
                              : isEnglish 
                              ? 'Mechanical, electronic and motion infrastructure technical details of the Tube & Profile Fiber Laser system are presented below.'
                              : 'Boru & Profil Fiber Lazer sisteminin mekanik, elektronik ve hareket altyapısına ait teknik detaylar aşağıda sunulmaktadır.')
                            : (isRussian 
                              ? `Ниже представлены технические данные механической, электронной и двигательной инфраструктуры системы ${selectedProduct.slug === 'kapali-kabin' ? 'полностью закрытого' : 'портального'} волоконного лазера.`
                              : isEnglish 
                              ? `Mechanical, electronic and motion infrastructure technical details of the ${selectedProduct.slug === 'kapali-kabin' ? 'Fully Enclosed' : 'Enclosed Gantry'} Fiber Laser system are presented below.`
                              : `${selectedProduct.slug === 'kapali-kabin' ? 'Kapalı Kabin' : 'Gezer Kabin'} Fiber Lazer sisteminin mekanik, elektronik ve hareket altyapısına ait teknik detaylar aşağıda sunulmaktadır.`)
                          }
                        </p>
                      </div>

                      <div className="space-y-3">
                        {/* Hareket ve Hassasiyet */}
                        <div className="border border-gray-200 rounded-xl overflow-hidden">
                          <button
                            onClick={() => toggleAccordion('movement')}
                            className="w-full px-6 py-4 bg-white hover:bg-gray-50 flex items-center justify-between transition-colors cursor-pointer"
                          >
                            <div className="flex items-center gap-3">
                              <i className="ri-speed-line text-[#DC2626] text-xl"></i>
                              <span className="text-[22px] font-bold text-gray-900">
                                {isRussian ? 'Перемещение и точность' : isEnglish ? 'Movement and Precision' : 'Hareket ve Hassasiyet'}
                              </span>
                            </div>
                            <i className={`ri-arrow-down-s-line text-gray-500 text-xl transition-transform duration-300 ${openAccordion === 'movement' ? 'rotate-180' : ''}`}></i>
                          </button>
                          {openAccordion === 'movement' && (
                            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {selectedProduct.slug === 'boru-profil-kesim' ? (
                                  <>
                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                      <span className="text-lg text-gray-600">
                                        {isRussian ? 'Перемещение по оси X' : isEnglish ? 'X-Axis Movement' : 'X Ekseni Hareketi'}
                                      </span>
                                      <span className="text-lg font-semibold text-gray-900">
                                        {region === 'us' ? '11 in' : '280 mm'}
                                      </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                      <span className="text-lg text-gray-600">
                                        {isRussian ? 'Перемещение по оси Y' : isEnglish ? 'Y-Axis Movement' : 'Y Ekseni Hareketi'}
                                      </span>
                                      <span className="text-lg font-semibold text-gray-900">
                                        {region === 'us' ? '20 ft' : '6000 mm'}
                                      </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                      <span className="text-lg text-gray-600">
                                        {isRussian ? 'Высота перемещения по оси Z' : isEnglish ? 'Z-Axis Movement Height' : 'Z-Eksen Hareket Yüksekliği'}
                                      </span>
                                      <span className="text-lg font-semibold text-gray-900">
                                        {region === 'us' ? '12.6 in' : '320 mm'}
                                      </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                      <span className="text-lg text-gray-600">
                                        {isRussian ? 'Макс. скорость X/Y' : isEnglish ? 'X/Y Maximum Speed' : 'X/Y Maksimum Hız'}
                                      </span>
                                      <span className="text-lg font-semibold text-gray-900">
                                        {region === 'us' ? '100 ft/min' : (isRussian ? '30 м/мин' : isEnglish ? '30 m/min' : '30 metre/dakika')}
                                      </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                      <span className="text-lg text-gray-600">
                                        {isRussian ? 'Макс. ускорение X/Y' : isEnglish ? 'X/Y Maximum Acceleration' : 'X/Y Maksimum İvme'}
                                      </span>
                                      <span className="text-lg font-semibold text-gray-900">
                                        {selectedProduct.slug === 'kapali-kabin' ? '1,5 G' : '0,8 G'}
                                      </span>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                      <span className="text-lg text-gray-600">
                                        {isRussian ? 'Точность позиционирования X/Y' : isEnglish ? 'X/Y Positioning Accuracy' : 'X/Y Eksenleri Pozisyon Hassasiyeti'}
                                      </span>
                                      <span className="text-lg font-semibold text-gray-900">
                                        {region === 'us' ? '±0.001 in' : '±0.03 mm'}
                                      </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                      <span className="text-lg text-gray-600">
                                        {isRussian ? 'Повторяемость X/Y' : isEnglish ? 'X/Y Repeat Accuracy' : 'X/Y Eksenleri Tekrarlama Hassasiyeti'}
                                      </span>
                                      <span className="text-lg font-semibold text-gray-900">
                                        {region === 'us' ? '±0.001 in' : '±0.02 mm'}
                                      </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                      <span className="text-lg text-gray-600">
                                        {isRussian ? 'Высота перемещения по оси Z' : isEnglish ? 'Z-Axis Movement Height' : 'Z-Eksen Hareket Yüksekliği'}
                                      </span>
                                      <span className="text-lg font-semibold text-gray-900">
                                        {region === 'us' && (selectedProduct.slug === 'kapali-kabin' || selectedProduct.slug === 'gezer-kabin') ? '12.6 in' : '320 mm'}
                                      </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                      <span className="text-lg text-gray-600">
                                        {isRussian ? 'Макс. скорость X/Y' : isEnglish ? 'X/Y Maximum Speed' : 'X/Y Maksimum Hız'}
                                      </span>
                                      <span className="text-lg font-semibold text-gray-900">
                                        {selectedProduct.slug === 'kapali-kabin' 
                                          ? (region === 'us' ? '290 ft/min' : isRussian ? '88 м/мин' : isEnglish ? '88 m/min' : '88 metre/dakika')
                                          : selectedProduct.slug === 'gezer-kabin'
                                          ? (region === 'us' ? '100 ft/min' : isRussian ? '30 м/мин' : isEnglish ? '30 m/min' : '30 metre/dakika')
                                          : (isRussian ? '30 м/мин' : isEnglish ? '30 m/min' : '30 metre/dakika')
                                        }
                                      </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                      <span className="text-lg text-gray-600">
                                        {isRussian ? 'Макс. ускорение X/Y' : isEnglish ? 'X/Y Maximum Acceleration' : 'X/Y Maksimum İvme'}
                                      </span>
                                      <span className="text-lg font-semibold text-gray-900">
                                        {selectedProduct.slug === 'kapali-kabin' ? '1,5 G' : '0,8 G'}
                                      </span>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Döner Eksen ve Boru Kesimi - Sadece Boru ve Profil için */}
                        {selectedProduct.slug === 'boru-profil-kesim' && (
                          <div className="border border-gray-200 rounded-xl overflow-hidden">
                            <button
                              onClick={() => toggleAccordion('rotary')}
                              className="w-full px-6 py-4 bg-white hover:bg-gray-50 flex items-center justify-between transition-colors cursor-pointer"
                            >
                              <div className="flex items-center gap-3">
                                <i className="ri-refresh-line text-[#DC2626] text-xl"></i>
                                <span className="text-[22px] font-bold text-gray-900">
                                  {isRussian ? 'Поворотная ось и резка труб' : isEnglish ? 'Rotary Axis and Tube Cutting' : 'Döner Eksen ve Boru Kesimi'}
                                </span>
                              </div>
                              <i className={`ri-arrow-down-s-line text-gray-500 text-xl transition-transform duration-300 ${openAccordion === 'rotary' ? 'rotate-180' : ''}`}></i>
                            </button>
                            {openAccordion === 'rotary' && (
                              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                    <span className="text-lg text-gray-600">
                                      {isRussian ? 'Вращение по/против часовой стрелки' : isEnglish ? 'Clockwise / Counterclockwise Movement' : 'Saat Yönü / Saat Ters Yönü Hareket'}
                                    </span>
                                    <span className="text-lg font-semibold text-gray-900 text-right">
                                      {isRussian ? 'Неограниченное вращение' : isEnglish ? 'Unlimited Rotation' : 'Sınırsız Rotasyon'}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                    <span className="text-lg text-gray-600">
                                      {isRussian ? 'Макс. скорость поворотной оси' : isEnglish ? 'Rotary Axis Maximum Speed' : 'Döner Eksen Maksimum Hız'}
                                    </span>
                                    <span className="text-lg font-semibold text-gray-900">50 rpm</span>
                                  </div>
                                  <div className="flex justify-between items-center py-2">
                                    <span className="text-lg text-gray-600">
                                      {isRussian ? 'Головка для резки труб' : isEnglish ? 'Tube Cutting Head' : 'Boru Kesme Kafası'}
                                    </span>
                                    <span className="text-lg font-semibold text-gray-900">BOCHU</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Mekanik Yapı - Sadece Kapalı ve Gezer Kabin için */}
                        {(selectedProduct.slug === 'kapali-kabin' || selectedProduct.slug === 'gezer-kabin') && (
                          <div className="border border-gray-200 rounded-xl overflow-hidden">
                            <button
                              onClick={() => toggleAccordion('mechanical')}
                              className="w-full px-6 py-4 bg-white hover:bg-gray-50 flex items-center justify-between transition-colors cursor-pointer"
                            >
                              <div className="flex items-center gap-3">
                                <i className="ri-tools-line text-[#DC2626] text-xl"></i>
                                <span className="text-[22px] font-bold text-gray-900">
                                  {isRussian ? 'Механическая конструкция' : isEnglish ? 'Mechanical Structure' : 'Mekanik Yapı'}
                                </span>
                              </div>
                              <i className={`ri-arrow-down-s-line text-gray-500 text-xl transition-transform duration-300 ${openAccordion === 'mechanical' ? 'rotate-180' : ''}`}></i>
                            </button>
                            {openAccordion === 'mechanical' && (
                              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                    <span className="text-lg text-gray-600">
                                      {isRussian ? 'Корпус' : isEnglish ? 'Body' : 'Gövde'}
                                    </span>
                                    <span className="text-lg font-semibold text-gray-900">Ajan Lazer</span>
                                  </div>
                                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                    <span className="text-lg text-gray-600">
                                      {isRussian ? 'Направляющие X/Y' : isEnglish ? 'X/Y Precision Axis Rail' : 'X/Y Hassas Eksen Peyi'}
                                    </span>
                                    <span className="text-lg font-semibold text-gray-900">HIWIN</span>
                                  </div>
                                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                    <span className="text-lg text-gray-600">
                                      {isRussian ? 'Номинальное напряжение питания' : isEnglish ? 'Nominal Supply Voltage' : 'Nominal Besleme Gerilimi'}
                                    </span>
                                    <span className="text-lg font-semibold text-gray-900">380 V</span>
                                  </div>
                                  <div className="flex justify-between items-center py-2">
                                    <span className="text-lg text-gray-600">
                                      {isRussian ? 'Частота' : isEnglish ? 'Frequency' : 'Frekans'}
                                    </span>
                                    <span className="text-lg font-semibold text-gray-900">50 Hz</span>
                                  </div>
                                  <div className="flex justify-between items-center py-2">
                                    <span className="text-lg text-gray-600">
                                      {isRussian ? 'Степень защиты основного источника питания' : isEnglish ? 'Main Power Supply Protection Level' : 'Ana Güç Kaynağı Koruma Seviyesi'}
                                    </span>
                                    <span className="text-lg font-semibold text-gray-900">IP54</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Elektrik ve Kontrol */}
                        <div className="border border-gray-200 rounded-xl overflow-hidden">
                          <button
                            onClick={() => toggleAccordion('electrical')}
                            className="w-full px-6 py-4 bg-white hover:bg-gray-50 flex items-center justify-between transition-colors cursor-pointer"
                          >
                            <div className="flex items-center gap-3">
                              <i className="ri-cpu-line text-[#DC2626] text-xl"></i>
                              <span className="text-[22px] font-bold text-gray-900">
                                {isRussian ? 'Электрика и управление' : isEnglish ? 'Electrical and Control' : 'Elektrik ve Kontrol'}
                              </span>
                            </div>
                            <i className={`ri-arrow-down-s-line text-gray-500 text-xl transition-transform duration-300 ${openAccordion === 'electrical' ? 'rotate-180' : ''}`}></i>
                          </button>
                          {openAccordion === 'electrical' && (
                            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                  <span className="text-lg text-gray-600">
                                    {isRussian ? 'Система ЧПУ управления' : isEnglish ? 'CNC Control System' : 'CNC Kontrol Sistemi'}
                                  </span>
                                  <span className="text-lg font-semibold text-gray-900">FSCUT</span>
                                </div>
                                {selectedProduct.slug === 'boru-profil-kesim' ? (
                                  <>
                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                      <span className="text-lg text-gray-600">
                                        {isRussian ? 'Количество фаз' : isEnglish ? 'Phase Count' : 'Faz Sayısı'}
                                      </span>
                                      <span className="text-lg font-semibold text-gray-900">3</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                      <span className="text-lg text-gray-600">
                                        {isRussian ? 'Номинальное напряжение питания' : isEnglish ? 'Nominal Supply Voltage' : 'Nominal Besleme Gerilimi'}
                                      </span>
                                      <span className="text-lg font-semibold text-gray-900">380 V</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                      <span className="text-lg text-gray-600">
                                        {isRussian ? 'Частота' : isEnglish ? 'Frequency' : 'Frekans'}
                                      </span>
                                      <span className="text-lg font-semibold text-gray-900">50 Hz</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2">
                                      <span className="text-lg text-gray-600">
                                        {isRussian ? 'Степень защиты основного источника питания' : isEnglish ? 'Main Power Supply Protection Level' : 'Ana Güç Kaynağı Koruma Seviyesi'}
                                      </span>
                                      <span className="text-lg font-semibold text-gray-900">IP54</span>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                      <span className="text-lg text-gray-600">
                                        {isRussian ? 'Серводвигатели и приводы' : isEnglish ? 'Servo Motors and Drivers' : 'Servo Motorlar ve Sürücüler'}
                                      </span>
                                      <span className="text-lg font-semibold text-gray-900">Delta</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2">
                                      <span className="text-lg text-gray-600">
                                        {isRussian ? 'Электрические компоненты' : isEnglish ? 'Electrical Components' : 'Elektriksel Komponentler'}
                                      </span>
                                      <span className="text-lg font-semibold text-gray-900">Schneider, Omron</span>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Destek ve Yardımcı Sistemler - Sadece Kapalı ve Gezer Kabin için */}
                        {(selectedProduct.slug === 'kapali-kabin' || selectedProduct.slug === 'gezer-kabin') && (
                          <div className="border border-gray-200 rounded-xl overflow-hidden">
                            <button
                              onClick={() => toggleAccordion('support')}
                              className="w-full px-6 py-4 bg-white hover:bg-gray-50 flex items-center justify-between transition-colors cursor-pointer"
                            >
                              <div className="flex items-center gap-3">
                                <i className="ri-settings-4-line text-[#DC2626] text-xl"></i>
                                <span className="text-[22px] font-bold text-gray-900">
                                  {isRussian ? 'Вспомогательные системы' : isEnglish ? 'Support and Auxiliary Systems' : 'Destek ve Yardımcı Sistemler'}
                                </span>
                              </div>
                              <i className={`ri-arrow-down-s-line text-gray-500 text-xl transition-transform duration-300 ${openAccordion === 'support' ? 'rotate-180' : ''}`}></i>
                            </button>
                            {openAccordion === 'support' && (
                              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                    <span className="text-lg text-gray-600">
                                      {isRussian ? 'Лазерная режущая головка' : isEnglish ? 'Laser Cutting Head' : 'Lazer Kesim Kafası'}
                                    </span>
                                    <span className="text-lg font-semibold text-gray-900">BOCHU Autofocus</span>
                                  </div>
                                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                    <span className="text-lg text-gray-600">
                                      {isRussian ? 'Чиллер (воздушный охладитель)' : isEnglish ? 'Chiller (Air Chiller)' : 'Soğutucu (Air Chiller)'}
                                    </span>
                                    <span className="text-lg font-semibold text-gray-900">TEYU S&A</span>
                                  </div>
                                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                    <span className="text-lg text-gray-600">
                                      {isRussian ? 'Пневматические компоненты' : isEnglish ? 'Pneumatic Components' : 'Pnömatik Komponentler'}
                                    </span>
                                    <span className="text-lg font-semibold text-gray-900">Airtac</span>
                                  </div>
                                  <div className="flex justify-between items-center py-2">
                                    <span className="text-lg text-gray-600">
                                      {isRussian ? 'Автоматическая система смазки' : isEnglish ? 'Automatic Lubrication System' : 'Otomatik Yağlama Sistemi'}
                                    </span>
                                    <span className="text-lg font-semibold text-gray-900">
                                      {isRussian ? 'Китай' : isEnglish ? 'China' : 'Çin'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Kullanım Alanları + Yerleşim Planları */}
                  {(selectedProduct.slug === 'kapali-kabin' || selectedProduct.slug === 'gezer-kabin' || selectedProduct.slug === 'boru-profil-kesim') && (
                    <>
                      {/* Yerleşim Planları Bölümü - Sadece Gezer Kabin için */}
                      {selectedProduct.slug === 'gezer-kabin' && (
                        <div className="mb-8">
                          <div className="space-y-3">
                            <div className="border border-gray-300 rounded-xl overflow-hidden bg-gray-50">
                              <button
                                onClick={() => toggleAccordion('layout-plans')}
                                className="w-full px-6 py-4 bg-gray-100 hover:bg-gray-200 flex items-center justify-between transition-colors cursor-pointer"
                              >
                                <div className="flex items-center gap-3">
                                  <i className="ri-file-list-3-line text-gray-700 text-xl"></i>
                                  <span className="text-[22px] font-bold text-gray-900">
                                    {isRussian ? 'Планы размещения' : isEnglish ? 'Layout Plans' : 'Yerleşim Planları'}
                                  </span>
                                </div>
                                <i className={`ri-arrow-down-s-line text-gray-500 text-xl transition-transform duration-300 ${openAccordion === 'layout-plans' ? 'rotate-180' : ''}`}></i>
                              </button>
                              {openAccordion === 'layout-plans' && (
                                <div className="px-6 py-4 bg-white border-t border-gray-300">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <a
                                      href="http://files.ajanlazer.com/wp-content/uploads/2025/11/1500X3000_GEZER_KABIN_LAZER.pdf"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center justify-between py-3 px-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer group"
                                    >
                                      <div className="flex items-center gap-3">
                                        <i className="ri-file-pdf-line text-gray-600 text-2xl"></i>
                                        <span className="text-lg font-medium text-gray-900">1500 × 3000 mm</span>
                                      </div>
                                      <span className="text-base text-gray-600 group-hover:text-gray-900 flex items-center gap-2">
                                        {isRussian ? 'Открыть PDF' : isEnglish ? 'View PDF' : 'PDF\'yi Görüntüle'}
                                        <i className="ri-external-link-line"></i>
                                      </span>
                                    </a>

                                    <a
                                      href="http://files.ajanlazer.com/wp-content/uploads/2025/11/2000x4000_GEZER_KABIN_LAZER.pdf"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center justify-between py-3 px-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer group"
                                    >
                                      <div className="flex items-center gap-3">
                                        <i className="ri-file-pdf-line text-gray-600 text-2xl"></i>
                                        <span className="text-lg font-medium text-gray-900">2000 × 4000 mm</span>
                                      </div>
                                      <span className="text-base text-gray-600 group-hover:text-gray-900 flex items-center gap-2">
                                        {isRussian ? 'Открыть PDF' : isEnglish ? 'View PDF' : 'PDF\'yi Görüntüle'}
                                        <i className="ri-external-link-line"></i>
                                      </span>
                                    </a>

                                    <a
                                      href="http://files.ajanlazer.com/wp-content/uploads/2025/11/3000x12000-2-JETLI_GEZER_KABIN_LAZER.pdf"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center justify-between py-3 px-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer group"
                                    >
                                      <div className="flex items-center gap-3">
                                        <i className="ri-file-pdf-line text-gray-600 text-2xl"></i>
                                        <span className="text-lg font-medium text-gray-900">3000 × 12000 mm</span>
                                      </div>
                                      <span className="text-base text-gray-600 group-hover:text-gray-900 flex items-center gap-2">
                                        {isRussian ? 'Открыть PDF' : isEnglish ? 'View PDF' : 'PDF\'yi Görüntüle'}
                                        <i className="ri-external-link-line"></i>
                                      </span>
                                    </a>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {selectedProduct.slug === 'kapali-kabin' && (
                        <div className="mb-8">
                          <div className="space-y-3">
                            <div className="border border-gray-300 rounded-xl overflow-hidden bg-gray-50">
                              <button
                                onClick={() => toggleAccordion('layout-plans')}
                                className="w-full px-6 py-4 bg-gray-100 hover:bg-gray-200 flex items-center justify-between transition-colors cursor-pointer"
                              >
                                <div className="flex items-center gap-3">
                                  <i className="ri-file-list-3-line text-gray-700 text-xl"></i>
                                  <span className="text-[22px] font-bold text-gray-900">
                                    {isRussian ? 'Планы размещения' : isEnglish ? 'Layout Plans' : 'Yerleşim Planları'}
                                  </span>
                                </div>
                                <i className={`ri-arrow-down-s-line text-gray-500 text-xl transition-transform duration-300 ${openAccordion === 'layout-plans' ? 'rotate-180' : ''}`}></i>
                              </button>
                              {openAccordion === 'layout-plans' && (
                                <div className="px-6 py-4 bg-white border-t border-gray-300">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {['1500x3000', '1500x6000', '2000x3000', '2000x6000', '2500x6000'].map((size) => (
                                      <a
                                        key={size}
                                        href={`https://files.ajanlazer.com/wp-content/uploads/2025/11/${size}.pdf`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-between py-3 px-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer group"
                                      >
                                        <div className="flex items-center gap-3">
                                          <i className="ri-file-pdf-line text-gray-600 text-2xl"></i>
                                          <span className="text-lg font-medium text-gray-900">{size.replace('x', ' × ')} mm</span>
                                        </div>
                                        <span className="text-base text-gray-600 group-hover:text-gray-900 flex items-center gap-2">
                                          {isRussian ? 'Открыть PDF' : isEnglish ? 'View PDF' : 'PDF\'yi Görüntüle'}
                                          <i className="ri-external-link-line"></i>
                                        </span>
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Yerleşim Planları Bölümü - Boru ve Profil için */}
                      {selectedProduct.slug === 'boru-profil-kesim' && (
                        <div className="mb-8">
                          <div className="space-y-3">
                            <div className="border border-gray-300 rounded-xl overflow-hidden bg-gray-50">
                              <button
                                onClick={() => toggleAccordion('layout-plans')}
                                className="w-full px-6 py-4 bg-gray-100 hover:bg-gray-200 flex items-center justify-between transition-colors cursor-pointer"
                              >
                                <div className="flex items-center gap-3">
                                  <i className="ri-file-list-3-line text-gray-700 text-xl"></i>
                                  <span className="text-[22px] font-bold text-gray-900">
                                    {isRussian ? 'Планы размещения' : isEnglish ? 'Layout Plans' : 'Yerleşim Planları'}
                                  </span>
                                </div>
                                <i className={`ri-arrow-down-s-line text-gray-500 text-xl transition-transform duration-300 ${openAccordion === 'layout-plans' ? 'rotate-180' : ''}`}></i>
                              </button>
                              {openAccordion === 'layout-plans' && (
                                <div className="px-6 py-4 bg-white border-t border-gray-300">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <a
                                      href="http://files.ajanlazer.com/wp-content/uploads/2025/12/6mBKM_Lazer.pdf"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center justify-between py-3 px-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer group"
                                    >
                                      <div className="flex items-center gap-3">
                                        <i className="ri-file-pdf-line text-gray-600 text-2xl"></i>
                                        <span className="text-lg font-medium text-gray-900">6000 mm BKM</span>
                                      </div>
                                      <span className="text-base text-gray-600 group-hover:text-gray-900 flex items-center gap-2">
                                        {isRussian ? 'Открыть PDF' : isEnglish ? 'View PDF' : 'PDF\'yi Görüntüle'}
                                        <i className="ri-external-link-line"></i>
                                      </span>
                                    </a>

                                    <a
                                      href="http://files.ajanlazer.com/wp-content/uploads/2025/12/12mBKM_Lazer.pdf"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center justify-between py-3 px-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer group"
                                    >
                                      <div className="flex items-center gap-3">
                                        <i className="ri-file-pdf-line text-gray-600 text-2xl"></i>
                                        <span className="text-lg font-medium text-gray-900">12000 mm BKM</span>
                                      </div>
                                      <span className="text-base text-gray-600 group-hover:text-gray-900 flex items-center gap-2">
                                        {isRussian ? 'Открыть PDF' : isEnglish ? 'View PDF' : 'PDF\'yi Görüntüle'}
                                        <i className="ri-external-link-line"></i>
                                      </span>
                                    </a>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Kullanım Alanları */}
                      <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8">
                        <h3 className="text-[38px] font-bold text-gray-900 mb-4 leading-tight">
                          {isRussian ? 'Области применения' : isEnglish ? 'Application Areas' : 'Kullanım Alanları'}
                        </h3>
                        <ul className="space-y-3">
                          {selectedProduct.slug === 'kapali-kabin' ? (
                            <>
                              <li className="flex items-start gap-3">
                                <i className="ri-checkbox-circle-fill text-[#DC2626] text-[22px] mt-0.5 flex-shrink-0"></i>
                                <span className="text-[22px] text-gray-700 leading-[1.7]">
                                  {isRussian 
                                    ? 'Серийные производственные линии, требующие высокой точности и стабильности'
                                    : isEnglish 
                                    ? 'Serial production lines requiring high precision and consistency'
                                    : 'Seri üretim hatları ve yüksek hassasiyet gerektiren uygulamalar'}
                                </span>
                              </li>
                              <li className="flex items-start gap-3">
                                <i className="ri-checkbox-circle-fill text-[#DC2626] text-[22px] mt-0.5 flex-shrink-0"></i>
                                <span className="text-[22px] text-gray-700 leading-[1.7]">
                                  {isRussian 
                                    ? 'Точная резка листового металла в автомобильной и электронной промышленности'
                                    : isEnglish 
                                    ? 'Precision sheet metal cutting operations in automotive and electronics sectors'
                                    : 'Otomotiv, elektronik ve hassas sac metal kesim işlemleri'}
                                </span>
                              </li>
                              <li className="flex items-start gap-3">
                                <i className="ri-checkbox-circle-fill text-[#DC2626] text-[22px] mt-0.5 flex-shrink-0"></i>
                                <span className="text-[22px] text-gray-700 leading-[1.7]">
                                  {isRussian 
                                    ? 'Предприятия, где приоритетом является скорость загрузки-выгрузки и доступ'
                                    : isEnglish 
                                    ? 'Facilities where loading-unloading speed and access are priorities'
                                    : 'Yükleme–boşaltma hızının ve erişimin öncelikli olduğu tesisler'}
                                </span>
                              </li>
                              <li className="flex items-start gap-3">
                                <i className="ri-checkbox-circle-fill text-[#DC2626] text-[22px] mt-0.5 flex-shrink-0"></i>
                                <span className="text-[22px] text-gray-700 leading-[1.7]">
                                  {isRussian 
                                    ? 'Промышленные производственные среды и предприятия, требующие интеграции в открытое пространство'
                                    : isEnglish 
                                    ? 'Industrial production environments and businesses requiring open area integration'
                                    : 'Endüstriyel üretim ortamları ve açık alan entegrasyonu gerektiren işletmeler'}
                                </span>
                              </li>
                            </>
                          ) : selectedProduct.slug === 'gezer-kabin' ? (
                            <>
                              <li className="flex items-start gap-3">
                                <i className="ri-checkbox-circle-fill text-[#DC2626] text-[22px] mt-0.5 flex-shrink-0"></i>
                                <span className="text-[22px] text-gray-700 leading-[1.7]">
                                  {isRussian 
                                    ? 'Производственные линии для обработки крупноформатного листового металла'
                                    : isEnglish 
                                    ? 'Production lines processing large-scale sheet metal'
                                    : 'Büyük ebatlı sacların işlendiği üretim hatları'}
                                </span>
                              </li>
                              <li className="flex items-start gap-3">
                                <i className="ri-checkbox-circle-fill text-[#DC2626] text-[22px] mt-0.5 flex-shrink-0"></i>
                                <span className="text-[22px] text-gray-700 leading-[1.7]">
                                  {isRussian 
                                    ? 'Применения, требующие резки длинных листов и гибкой рабочей зоны'
                                    : isEnglish 
                                    ? 'Applications requiring long sheet cutting and flexible working area'
                                    : 'Uzun sac kesimi ve esnek çalışma alanı gerektiren uygulamalar'}
                                </span>
                              </li>
                              <li className="flex items-start gap-3">
                                <i className="ri-checkbox-circle-fill text-[#DC2626] text-[22px] mt-0.5 flex-shrink-0"></i>
                                <span className="text-[22px] text-gray-700 leading-[1.7]">
                                  {isRussian 
                                    ? 'Предприятия, где приоритетом является скорость загрузки-выгрузки и доступ'
                                    : isEnglish 
                                    ? 'Facilities where loading-unloading speed and access are priorities'
                                    : 'Yükleme–boşaltma hızının ve erişimin öncelikli olduğu tesisler'}
                                </span>
                              </li>
                              <li className="flex items-start gap-3">
                                <i className="ri-checkbox-circle-fill text-[#DC2626] text-[22px] mt-0.5 flex-shrink-0"></i>
                                <span className="text-[22px] text-gray-700 leading-[1.7]">
                                  {isRussian 
                                    ? 'Промышленные производственные среды и предприятия, требующие интеграции в открытое пространство'
                                    : isEnglish 
                                    ? 'Industrial production environments and businesses requiring open area integration'
                                    : 'Endüstriyel üretim ortamları ve açık alan entegrasyonu gerektiren işletmeler'}
                                </span>
                              </li>
                            </>
                          ) : (
                            <>
                              <li className="flex items-start gap-3">
                                <i className="ri-checkbox-circle-fill text-[#DC2626] text-[22px] mt-0.5 flex-shrink-0"></i>
                                <span className="text-[22px] text-gray-700 leading-[1.7]">
                                  {isRussian 
                                    ? 'Промышленное производство на основе труб и профилей'
                                    : isEnglish 
                                    ? 'Tube and profile-based industrial production'
                                    : 'Boru ve profil bazlı endüstriyel üretimler'}
                                </span>
                              </li>
                              <li className="flex items-start gap-3">
                                <i className="ri-checkbox-circle-fill text-[#DC2626] text-[22px] mt-0.5 flex-shrink-0"></i>
                                <span className="text-[22px] text-gray-700 leading-[1.7]">
                                  {isRussian 
                                    ? 'Стальные конструкции и металлоконструкции'
                                    : isEnglish 
                                    ? 'Steel construction and metal structure systems'
                                    : 'Çelik konstrüksiyon ve metal yapı sistemleri'}
                                </span>
                              </li>
                              <li className="flex items-start gap-3">
                                <i className="ri-checkbox-circle-fill text-[#DC2626] text-[22px] mt-0.5 flex-shrink-0"></i>
                                <span className="text-[22px] text-gray-700 leading-[1.7]">
                                  {isRussian 
                                    ? 'Автомобильная промышленность, энергетика и инфраструктурные проекты'
                                    : isEnglish 
                                    ? 'Automotive, energy and infrastructure projects'
                                    : 'Otomotiv, enerji ve altyapı projeleri'}
                                </span>
                              </li>
                              <li className="flex items-start gap-3">
                                <i className="ri-checkbox-circle-fill text-[#DC2626] text-[22px] mt-0.5 flex-shrink-0"></i>
                                <span className="text-[22px] text-gray-700 leading-[1.7]">
                                  {isRussian 
                                    ? 'Серийное производство и индивидуальные решения для резки'
                                    : isEnglish 
                                    ? 'Serial production and custom cutting applications'
                                    : 'Seri üretim ve özel kesim uygulamaları'}
                                </span>
                              </li>
                            </>
                          )}
                        </ul>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* CTA Bölümü */}
              <div id="cta-section" className="bg-gradient-to-r from-[#DC2626] to-[#B91C1C] rounded-2xl p-8 text-center text-white shadow-xl mb-0">
                <h3 className="text-5xl font-bold mb-4 leading-tight">
                  {isRussian ? 'Получите подробную информацию' : isEnglish ? 'Get Detailed Information' : 'Detaylı Bilgi Alın'}
                </h3>
                <p className="text-2xl mb-6 text-white/90 leading-[1.7]">
                  {isRussian 
                    ? 'Свяжитесь с нами для получения технических характеристик и цен' 
                    : isEnglish 
                    ? 'Contact us for technical specifications and pricing' 
                    : 'Teknik özellikler ve fiyat bilgisi için bizimle iletişime geçin'}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  {region === 'ru' ? (
                    <a
                      href="tel:+74957778817"
                      className="inline-flex items-center px-8 py-4 bg-white text-[#D62828] font-semibold text-lg rounded-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-phone-line mr-2"></i>
                      Позвоните для записи: +7 (495) 777-88-17
                    </a>
                  ) : region === 'us' ? (
                    <a
                      href="tel:+14709702526"
                      className="inline-flex items-center px-8 py-4 bg-white text-[#D62828] font-semibold text-lg rounded-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-phone-line mr-2"></i>
                      Call for Appointment: 470-970-2526
                    </a>
                  ) : (
                    <a
                      href={
                        region === 'ru' 
                          ? "tel:+74957778817" 
                          : region === 'us' 
                          ? "tel:+19299308817" 
                          : isEnglish 
                          ? "tel:+904442526" 
                          : "tel:444 25 26"
                      }
                      className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#DC2626] font-semibold text-[22px] rounded-lg hover:bg-gray-100 hover:text-[#B91C1C] transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-phone-line mr-2"></i>
                      {region === 'ru' 
                        ? '+7 (495) 777-88-17' 
                        : region === 'us' 
                        ? '+1 (929) 930-88-17' 
                        : isEnglish 
                        ? '+90 444 25 26' 
                        : '444 25 26'}
                    </a>
                  )}
                  <button
                    onClick={handleRequestQuote}
                    className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white text-white font-semibold text-[22px] rounded-lg hover:bg-white hover:text-[#DC2626] transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-mail-line mr-2"></i>
                    {isRussian ? 'Запросить предложение' : isEnglish ? 'Request Quote' : 'Teklif İste'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {region ? <Footer_v2 region={region} /> : <Footer />}

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-14 h-14 bg-[#D62828] text-white rounded-full shadow-2xl hover:bg-[#B91C1C] transition-all duration-300 hover:scale-110 z-50 flex items-center justify-center cursor-pointer group"
          aria-label="Yukarı Çık"
        >
          <i className="ri-arrow-up-line text-2xl group-hover:animate-bounce"></i>
        </button>
      )}
    </div>
  );
}
